"use client";

import fetchCandidatesByIds from "@lib/candidates/fetchCandidatesByIds";
import { JobStatus, JobTypes } from "@lib/enums";
import { useJobStatus } from "@lib/jobs/useJobStatus";
import { useAxios } from "@/app/providers/AxiosProvider";
import { useEffect, useRef, useState } from "react";

const preProcessingStatuses = [
  JobStatus.QUEUED,
  JobStatus.PENDING,
  JobStatus.IMPORTING_PERSONAL_DETAILS,
];

/**
 * This hook is used to process the import jobs returned by the server or websockets.
 * It returns:
 * - importing: Array of candidates currently being imported
 * - failed: Array of failed import jobs
 * It also triggers the onImportComplete callback when an import job is completed
 * to return a list of candidates that have been imported.
 *
 * @param {(candidates: Candidate[]) => void} onImportComplete
 * @returns {{ importing: Candidate[], failed: Job[] }}
 */
export default function useImportingStatus(onImportComplete) {
  const api = useAxios();
  const [notifiedAsComplete, setNotifiedAsComplete] = useState([]);
  const [importing, setImporting] = useState([]);
  const [failed, setFailed] = useState([]);
  const runIdRef = useRef(0);
  // The pass below awaits a fetch, so it calls whichever callback is current
  // when that lands, not the one from the render that started it — otherwise
  // the caller acts on state from before the import finished.
  const onImportCompleteRef = useRef(onImportComplete);
  useEffect(() => {
    onImportCompleteRef.current = onImportComplete;
  });

  const { jobs } = useJobStatus(JobTypes.AI);

  // for useEffect dependency
  // only update when ids or statuses have changed
  const idStatusMap = jobs.reduce((acc, job) => {
    acc[job.id] = job.status;
    return acc;
  }, {});

  const queuedJobs =
    jobs?.filter((job) => preProcessingStatuses.includes(job.status)) || [];
  const processingJobs =
    jobs?.filter((job) => job.status === JobStatus.IMPORTING_CV) || [];
  const completedJobs =
    jobs?.filter((job) => job.status === JobStatus.COMPLETE) || [];
  const failedJobs =
    jobs?.filter((job) => job.status === JobStatus.FAILED) || [];

  useEffect(() => {
    const myRunId = ++runIdRef.current;
    const processJobs = async () => {
      // get "processing" job candidate ids that are not already in the importing list
      const idsImporting = processingJobs
        .map((job) => job.metadata?.candidate_id)
        .filter(
          (id) =>
            id != null && !importing.some((candidate) => candidate.id === id)
        );

      // remove failed jobs from importing
      let updatedImporting = [...importing];
      for (const job of failedJobs) {
        const id = job.metadata?.candidate_id || `failed-${job.id}`;
        updatedImporting = updatedImporting.filter(
          (candidate) => candidate.id !== id
        );
      }

      // remove completed jobs from importing and failed
      // add to notifiedAsComplete and trigger onImportComplete
      let updatedFailed = [...failedJobs];
      const idsCompleted = new Set();
      for (const job of completedJobs) {
        const id = job.metadata?.candidate_id;
        updatedFailed = updatedFailed.filter((failed) => failed.id !== id);
        updatedImporting = updatedImporting.filter(
          (candidate) => candidate.id !== id
        );
        if (id == null || notifiedAsComplete.includes(id)) {
          continue;
        }
        idsCompleted.add(id);
      }

      // retrieve new completed candidates in sync
      const candidates = await fetchCandidatesByIds(api, [
        ...idsImporting,
        ...idsCompleted,
      ]);

      // Drop results from a stale run — a newer job-status update has
      // already started another pass, so applying these would clobber it
      // (e.g. re-adding a candidate to `importing` after it completed).
      if (myRunId !== runIdRef.current) return;

      const newImporting = candidates.filter((candidate) =>
        idsImporting.includes(candidate.id)
      );
      const newCompleted = candidates.filter((candidate) =>
        idsCompleted.has(candidate.id)
      );

      setFailed(updatedFailed);
      setImporting([...updatedImporting, ...newImporting]);
      setNotifiedAsComplete((prev) => [...prev, ...idsCompleted.values()]);
      if (newCompleted.length) {
        onImportCompleteRef.current(newCompleted);
      }
    };

    processJobs();
  }, [JSON.stringify(idStatusMap)]);

  return { queued: queuedJobs, importing, failed };
}
